import type { FC } from "react";

import { log, PushProcessingNotification } from "@repo/helpers";
import { BasicDialog, Button, ButtonTypes, DropDownList, Loading, Selector } from "@repo/ui";
import { useCallback, useState } from "react";

import type { Props } from "../types";

import { useTrainingApi, useTrainingQueries } from "../../../api/trainingApi";
import { useRepContext } from "../../../contexts/editableItemContext";
import { usePermissionsCheck } from "../../../helpers/permissions";
import { useSettingsQuery } from "../../../helpers/queryHooks";
import style from "./style.module.scss";

export const AssignTraining: FC<Props> = (props: Props) => {
	const { repId } = useRepContext();
	const { fullAPReadOnly } = usePermissionsCheck();
	const [settings, settingsLoaded] = useSettingsQuery();
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const { useMindFlashQuery } = useTrainingQueries();
	const { clearQuery } = useMindFlashQuery();

	const handleOpenModal = useCallback(function () {
		setIsDialogOpen(true);
	}, []);

	const handleCloseModal = useCallback(function () {
		setIsDialogOpen(false);
	}, []);

	if (!settingsLoaded || !settings.mindflashUserExists || !settings.trainingSettings.canAssign || fullAPReadOnly)
		return null;
	return (
		<div className={style.mindflashTrainingButton}>
			<Button types={[ButtonTypes.Primary]} onClick={handleOpenModal} disabled={props.readOnly}>
				Assign Training
			</Button>

			<BasicDialog
				title="Assign Training"
				open={isDialogOpen}
				onOpenChange={setIsDialogOpen}
				hideTrigger
				className="md:max-w-[800px]"
			>
				<TrainingsForm repId={repId} onFinish={clearQuery} onClose={handleCloseModal} />
			</BasicDialog>
		</div>
	);
};

const yesNoOptions = [
	{ id: true, value: "Yes" },
	{ id: false, value: "No" },
];
const TrainingsForm: FC<{ repId: number; onFinish: () => void; onClose: () => void }> = ({
	repId,
	onFinish,
	onClose,
}) => {
	const { assignTrainingCourse } = useTrainingApi();
	const { useAllMindflashTrainingsQuery } = useTrainingQueries();
	const [selectedTraining, setSelectedTraining] = useState("");
	const [isRequired, setIsRequired] = useState(true);
	const { data, loaded } = useAllMindflashTrainingsQuery();

	const handleChange = useCallback(
		function (value: string) {
			setSelectedTraining(value);
		},
		[setSelectedTraining],
	);

	const submitHandler = useCallback(
		async function () {
			const notify = PushProcessingNotification("Assigning...");
			if (!selectedTraining) {
				notify.error("Please select a training");
				return;
			}

			try {
				await assignTrainingCourse(repId, selectedTraining, isRequired);
				notify.success("Assigned!");
				onFinish();
				onClose();
			} catch (err) {
				notify.error("Failed to assign");
				log(err, "error");
			}
		},
		[repId, selectedTraining, isRequired, onFinish, onClose],
	);

	return (
		<Loading loading={!loaded}>
			<DropDownList
				id="mindflash-active-trainings"
				label="Active Trainings"
				value={selectedTraining}
				changeHandler={handleChange}
				values={data}
				className={style.dropdown}
				clearable
			/>
			<Selector
				id="mindflash-course-required"
				label="Required?"
				value={isRequired}
				values={yesNoOptions}
				changeHandler={setIsRequired}
				clearable
				renderAsPanels={false}
			/>

			<div className={style.controls}>
				<Button types={[ButtonTypes.Primary]} onClick={submitHandler}>
					Submit
				</Button>
			</div>
		</Loading>
	);
};
